import { captureFirstTouchAttribution, getFirstTouchAttribution } from "./attribution";
import { isBrowser } from "./browser";
import { buildEventContext } from "./context";
import { readAnonymousId, readLinkedAnonymousId } from "./identity";
import { getOrCreateSession } from "./session";

/**
 * What the browser knows about this visit, sent when checkout starts.
 *
 * A purchase is confirmed server-side -- the PayU webhook in particular arrives
 * with no browser attached -- so the marketing context has to be handed over
 * while the visitor is still here. Persisted against the payment and replayed
 * onto the purchase analytics event once the payment is verified.
 *
 * Keys are camelCase to match the analytics event contract.
 */
export type CheckoutAttribution = {
  anonymousId?: string;
  sessionId: string;
  utm: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  };
  device?: string;
  pageUrl?: string;
};

function compactUtm(
  attribution: ReturnType<typeof getFirstTouchAttribution>,
): CheckoutAttribution["utm"] {
  const utm: CheckoutAttribution["utm"] = {};
  if (attribution.source) utm.source = attribution.source;
  if (attribution.medium) utm.medium = attribution.medium;
  if (attribution.campaign) utm.campaign = attribution.campaign;
  if (attribution.content) utm.content = attribution.content;
  if (attribution.term) utm.term = attribution.term;
  return utm;
}

/**
 * Returns undefined outside the browser or on any failure, so a checkout never
 * fails because attribution could not be read.
 */
export function buildCheckoutAttribution(): CheckoutAttribution | undefined {
  if (!isBrowser()) return undefined;
  try {
    captureFirstTouchAttribution();
    // The live cookie is cleared once this browser is linked. The purchase is
    // written later from this snapshot, so keep the linked browser id here.
    const anonymousId = readAnonymousId() ?? readLinkedAnonymousId();
    const { sessionId } = getOrCreateSession();
    if (!sessionId) return undefined;

    const context = buildEventContext();
    const payload: CheckoutAttribution = {
      sessionId,
      utm: compactUtm(getFirstTouchAttribution()),
    };
    if (anonymousId) payload.anonymousId = anonymousId;
    if (context.device) payload.device = context.device;
    if (context.pageUrl) payload.pageUrl = context.pageUrl;
    return payload;
  } catch {
    return undefined;
  }
}
